import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resendVerification } from '../../api/signupApi';
import { FaEnvelope, FaShieldAlt, FaCheckCircle } from "react-icons/fa";
import { FaCow } from "react-icons/fa6";
import { FiMail, FiArrowLeft, FiRefreshCw, FiClock, FiCheck } from "react-icons/fi";
import { useTranslation } from 'react-i18next';
import logo from "../../screenshots/Milkfarm.jpeg";
import './VerifyMail.css';

export default function VerifyMail() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const email = location.state?.email;
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleResend = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError(t('verifyMail.noToken'));
            return;
        }
        setLoading(true);
        setMessage('');
        setError('');

        try {
            await resendVerification(token);
            setMessage(t('verifyMail.successMessage'));
            setCountdown(60);
        } catch (err) {
            if (err.response?.status === 429) {
                setError(t('verifyMail.tooMany'));
            } else {
                setError(t('verifyMail.errorMessage'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="vm-page">
            <div className="vm-left">
                <div className="vm-content">
                    <div className="vm-logo-section">
                        <div className="vm-logo-icon">
                            <img src={logo} alt="MilkFarm Logo" className="vm-logo-image" />
                        </div>
                        <h1 className="vm-logo-text">{t('verifyMail.brand')}</h1>
                    </div>

                    <div className="vm-icon-wrap">
                        <div className="vm-icon-ring">
                            <FiMail />
                        </div>
                        <div className="vm-icon-check"><FiCheck /></div>
                    </div>

                    <div className="vm-heading">
                        <h2>{t('verifyMail.title')}</h2>
                        <p>{t('verifyMail.subtitle')}</p>
                    </div>

                    <div className="vm-email-badge">
                        <FaEnvelope />
                        <span>{email || t('verifyMail.noEmail')}</span>
                    </div>

                    <p className="vm-instruction">{t('verifyMail.instruction')}</p>

                    {message && (
                        <div className="vm-alert vm-alert--success">
                            <FaCheckCircle />
                            <span>{message}</span>
                        </div>
                    )}
                    {error && (
                        <div className="vm-alert vm-alert--error">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="vm-actions">
                        <button
                            className="vm-btn-primary"
                            onClick={handleResend}
                            disabled={loading || countdown > 0}
                        >
                            {loading ? (
                                <>
                                    <svg className="vm-spinner" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50.265" strokeDashoffset="25" />
                                    </svg>
                                    {t('verifyMail.sending')}
                                </>
                            ) : countdown > 0 ? (
                                <>
                                    <FiClock />
                                    {t('verifyMail.resendIn', { seconds: countdown })}
                                </>
                            ) : (
                                <>
                                    <FiRefreshCw />
                                    {t('verifyMail.resendBtn')}
                                </>
                            )}
                        </button>

                        <button
                            className="vm-btn-secondary"
                            onClick={() => navigate('/signin')}
                        >
                            <FiArrowLeft />
                            {t('verifyMail.backToLogin')}
                        </button>
                    </div>

                    <div className="vm-steps">
                        <div className="vm-step">
                            <span className="vm-step-num">1</span>
                            <span>{t('verifyMail.step1')}</span>
                        </div>
                        <div className="vm-step">
                            <span className="vm-step-num">2</span>
                            <span>{t('verifyMail.step2')}</span>
                        </div>
                        <div className="vm-step">
                            <span className="vm-step-num">3</span>
                            <span>{t('verifyMail.step3')}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="vm-right">
                <div className="vm-hero-content">
                    <div className="vm-hero-badge">
                        <span className="vm-badge-dot"></span>
                        {t('verifyMail.hero.badge')}
                    </div>

                    <h1 className="vm-hero-title">
                        {t('verifyMail.hero.title')}
                        <span className="vm-gradient-text">{t('verifyMail.hero.titleHighlight')}</span>
                    </h1>

                    <p className="vm-hero-description">{t('verifyMail.hero.description')}</p>

                    <div className="vm-features-grid">
                        <div className="vm-feature-card">
                            <div className="vm-feature-icon"><FaShieldAlt /></div>
                            <h3>{t('verifyMail.hero.secureTitle')}</h3>
                            <p>{t('verifyMail.hero.secureDesc')}</p>
                        </div>
                        <div className="vm-feature-card">
                            <div className="vm-feature-icon"><FaEnvelope /></div>
                            <h3>{t('verifyMail.hero.verifyTitle')}</h3>
                            <p>{t('verifyMail.hero.verifyDesc')}</p>
                        </div>
                        <div className="vm-feature-card">
                            <div className="vm-feature-icon"><FaCow /></div>
                            <h3>{t('verifyMail.hero.startTitle')}</h3>
                            <p>{t('verifyMail.hero.startDesc')}</p>
                        </div>
                    </div>
                </div>

                <div className="vm-bg-decoration vm-decoration-1"></div>
                <div className="vm-bg-decoration vm-decoration-2"></div>
                <div className="vm-bg-decoration vm-decoration-3"></div>
                <div className="vm-bg-grid"></div>
            </div>
        </div>
    );
}